import * as _ from "lodash";

import { Column, ChartEntity, ColumnData, DataPointsMeta, ChartType } from "@ctra/api";
import { Optional } from "@ctra/utils";

import { ChartContextType } from "../../../../providers";
import { SeriesType } from "../../../../typings";
import { filterColumnBySeries } from "../../../../utils";

export class ColumnChartProcessor {
  public identity = "ColumnChartProcessor";

  /**
   * Tell if the Column chart may be grouped
   * @param {ChartContextType<ColumnData>["values"]["meta"]} meta
   */
  mayGroup() {
    return false;
  }

  /**
   * Filter unwanted items from the line data (the info which does not belong to any series)
   * @param {ChartContextType<ColumnData>["values"]["data"]} data
   * @param {ChartContextType<ColumnData>["values"]["meta"]} meta
   * @param {ChartContextType<ColumnData>["values"]["series"]} series
   * @returns {Record<ChartEntity["id"], Optional<Column>>}
   */
  getFilteredRawData(
    data: ChartContextType<ColumnData>["values"]["data"],
    meta: ChartContextType<ColumnData>["values"]["meta"],
    series: ChartContextType<ColumnData>["values"]["series"]
  ): Record<ChartEntity["id"], Optional<Column>> {
    return _.mapValues(data, (columnCollection, key) => {
      if (!!columnCollection) {
        const columnCollectionMeta: Optional<DataPointsMeta> = _.get(meta, [key]);

        if (!!columnCollectionMeta) {
          return filterColumnBySeries(columnCollection, columnCollectionMeta, series, {
            sort: columnCollectionMeta.type !== ChartType.basicBar
          });
        }

        return columnCollection;
      }

      return columnCollection;
    });
  }

  /**
   * Same as filtered data
   * @param {ChartContextType<ColumnData>["values"]["data"]} data
   * @param {ChartContextType<ColumnData>["values"]["meta"]} meta
   * @param {ChartContextType<ColumnData>["values"]["series"]} series
   * @returns {ChartContextType<ColumnData>["values"]["data"]}
   */
  getConsolidatedData(
    data: ChartContextType<ColumnData>["values"]["data"],
    meta: ChartContextType<ColumnData>["values"]["meta"],
    series: ChartContextType<ColumnData>["values"]["series"]
  ): ChartContextType<ColumnData>["values"]["data"] {
    return this.getFilteredRawData(data, meta, series);
  }

  /**
   * Remove the undefined values from the filtered data
   * @param {ChartContextType<ColumnData>["values"]["data"]} data
   * @param {ChartContextType<ColumnData>["values"]["meta"]} meta
   * @param {ChartContextType<ColumnData>["values"]["series"]} series
   * @returns {Column}
   */
  getData(
    data: ChartContextType<ColumnData>["values"]["data"],
    meta: ChartContextType<ColumnData>["values"]["meta"],
    series: ChartContextType<ColumnData>["values"]["series"]
  ): Column {
    return _.flatMap(_.omitBy(this.getConsolidatedData(data, meta, series), _.isUndefined)) as Column;
  }

  /**
   * Return the series, to implement in subclasses
   * @param {ChartContextType<ColumnData>["values"]["meta"]} meta
   * @param {ChartContextType<ColumnData>["values"]["series"]} series
   * @returns {SeriesType}
   */
  getSeries(
    meta: ChartContextType<ColumnData>["values"]["meta"],
    series: ChartContextType<ColumnData>["values"]["series"]
  ): SeriesType {
    return series;
  }
}
